"use client";

import { Box, Button, Flex, Heading, Stack, Text } from "@chakra-ui/react";
import Link from "next/link";
import {
  useEffect,
  useRef,
  forwardRef,
  type ReactElement,
} from "react";
import {
  CITY_REPORT_PRICE_CENTS,
  PLATFORM_DISPLAY_NAMES,
  type PublicDestinationReportSections,
  type PublicDestinationReportTeaser,
} from "@otalytics/shared";

import { safeCapture } from "@/lib/analytics";
import {
  CITY_REPORT_PRICING_LINK,
  leaderboardSectionBlurb,
  operatorSectionBlurb,
  purchaseButtonLabel,
  purchaseHeading,
  purchaseLede,
  rankGuideLink,
} from "@/lib/destinations/city-report-copy";
import { formatReportPrice } from "@/lib/reports/price";
import { useReportPurchase } from "@/lib/reports/use-report-purchase";
import { CityReportFaq } from "./city-report-faq";
import { LeadGateForm } from "./lead-gate-form";
import { LeaderboardTable } from "./leaderboard-table";
import { LockedSection } from "./locked-section";
import { ReportSections } from "./report-sections";
import { StickyPurchaseBar } from "./sticky-purchase-bar";
import { TopOperators } from "./top-operators";

/* The teaser has no per-city price of its own — the row that carries one only
   exists after a purchase — so the public page quotes the current list price.
   `/reports/[slug]` re-states the real figure from the server before checkout. */
const CITY_REPORT_PRICE = formatReportPrice(CITY_REPORT_PRICE_CENTS);

interface DestinationReportGateProps {
  slug: string;
  platform: string;
  destinationName: string;
  /**
   * From the server render. Non-null only when there was nothing worth gating —
   * a page too thin to withhold half of, where the API gives everything away
   * rather than blur a two-row table.
   */
  initialFull: PublicDestinationReportSections | null;
  /**
   * The free rows and the free three operators. Both are ungated content: they
   * ship in the server HTML so a crawler indexes real data rather than a
   * paywall, and they are the concrete proof the numbers exist.
   */
  topTours: PublicDestinationReportTeaser["topTours"];
  topOperators: PublicDestinationReportTeaser["topOperators"];
  /* Derived, not restated. A hand-copied structural duplicate silently went
     stale the moment the server added a field to it. */
  lockedCounts: PublicDestinationReportTeaser["lockedCounts"];
  /**
   * City landing copy: FAQ, the report CTA, and the section blurbs.
   * The POI page shares this gate and keeps the shorter ask.
   */
  pageCopy?: "city";
  /** Where the SERP was loaded from. Empty drops the "as seen from" FAQ clause. */
  proxyCountryName?: string;
}

/**
 * The teaser and its paywall.
 *
 * Purely presentational: it fetches nothing and knows nothing about who is
 * reading. The paid report lives at `/reports/[slug]`, and every unlock
 * affordance here is one `router.push` to it. That is what keeps this page
 * fully static under `revalidate = 300` with zero client requests — and it
 * retires the whole class of bug where a client-side unlock had to be
 * re-resolved on every navigation and mis-gated admins while auth loaded.
 *
 * **One CTA, three outcomes, no client-side branching.** An anonymous visitor
 * is bounced by the middleware to `/login?next=/reports/rome`; a subscriber
 * lands on their issue; everyone else lands on the buy page.
 */
export function DestinationReportGate({
  slug,
  platform,
  destinationName,
  initialFull,
  topTours,
  topOperators,
  lockedCounts,
  pageCopy,
  proxyCountryName = "",
}: DestinationReportGateProps): ReactElement {
  const { purchase, isSubmitting, error } = useReportPurchase(slug);

  // The two boundaries the sticky ask watches: where free proof ends, and
  // where the real ask begins.
  const manifestRef = useRef<HTMLDivElement>(null);
  const askRef = useRef<HTMLDivElement>(null);

  // Fired once per report. `unlocked` is now always the server's answer, which
  // for all but a thin page is false — honest, and the same shape the existing
  // funnels already read.
  const hasReportedView = useRef(false);
  useEffect(() => {
    if (hasReportedView.current) {
      return;
    }
    hasReportedView.current = true;
    safeCapture(
      "destination_report_viewed",
      {
        slug,
        platform,
        toursRanked: lockedCounts.tours,
        unlocked: initialFull !== null,
      },
      "destinations"
    );
  }, [slug, platform, lockedCounts.tours, initialFull]);

  /**
   * The one commercial action on the free page. It no longer routes to
   * `/reports/[slug]` and lets the session gate bounce a stranger to `/login`:
   * an anonymous reader goes straight to Stripe, and the account is minted from
   * the email they pay with. Signup was two exits (a password, then an inbox
   * round-trip) standing between wanting the report and owning it.
   */
  const platformLabel =
    PLATFORM_DISPLAY_NAMES[platform as keyof typeof PLATFORM_DISPLAY_NAMES] ??
    platform;
  const isCityCopy = pageCopy === "city";
  const operatorLink = isCityCopy ? rankGuideLink(platformLabel) : null;

  const handleUnlockClick = (): void => {
    safeCapture(
      "destination_report_unlock_clicked",
      { slug, platform },
      "destinations"
    );
    void purchase();
  };

  // Nothing was worth gating, so the API already returned every section.
  const faq = isCityCopy ? (
    <CityReportFaq
      city={destinationName}
      citySlug={slug}
      platform={platformLabel}
      proxyCountryName={proxyCountryName}
      price={CITY_REPORT_PRICE}
    />
  ) : null;

  if (initialFull) {
    return (
      <Stack gap={{ base: 10, md: 12 }}>
        <ReportSections
          full={initialFull}
          destinationName={destinationName}
          leaderboardNote={
            isCityCopy
              ? leaderboardSectionBlurb(
                  initialFull.leaderboard.length,
                  initialFull.leaderboard.length,
                )
              : undefined
          }
          operatorBlurb={
            isCityCopy ? operatorSectionBlurb(destinationName) : undefined
          }
          operatorLink={operatorLink}
        />
        {faq}
      </Stack>
    );
  }

  return (
    <>
      <Stack gap={{ base: 10, md: 12 }}>
        {/* The free rows, indexable, and the concrete proof the data is
            real. Named before the locked tail so the unlock has something
            to extend rather than reveal from nothing. */}
        {topTours.length > 0 && (
          <Box>
            {/* Spine tier — the one h2 that outranks the others. This table
                is the artifact the subhead promises; every other section is
                a reading of it.

                The heading text is identical in the unlocked document on
                purpose: unlocking completes the table the reader is already
                looking at, and the count carries the honesty. */}
            <Heading
              as="h2"
              fontFamily="var(--font-display)"
              fontSize={{ base: "26px", md: "34px" }}
              fontWeight="600"
              letterSpacing="-0.02em"
              color="var(--color-text-primary)"
              mb={1}
            >
              Every tour on the first page
            </Heading>
            {isCityCopy && (
              <Text
                fontSize={{ base: "15px", md: "16px" }}
                lineHeight="1.6"
                color="var(--color-text-secondary)"
                maxW="68ch"
                mb={3}
              >
                {leaderboardSectionBlurb(topTours.length, lockedCounts.tours)}
              </Text>
            )}
            <Text
              fontFamily="var(--font-geist-mono)"
              fontSize="11px"
              color="var(--color-text-tertiary)"
              mb={3}
            >
              showing {topTours.length} of {lockedCounts.tours}
            </Text>
            <LeaderboardTable rows={topTours} />
          </Box>
        )}
        <TopOperators
          operators={topOperators}
          title="Who owns the first page"
          blurb={
            isCityCopy
              ? operatorSectionBlurb(destinationName)
              : "The suppliers holding the most of this page, and how much of its recent review activity each one is taking."
          }
          link={operatorLink}
        />
        {/* Arms the sticky ask: everything above is free proof, everything
            below is what the reader would be paying for. */}
        <Box ref={manifestRef} aria-hidden="true" h={0} />
        {/* The manifest. Each block names what is withheld and how much of it
            there is; none of them carries a button. The single ask below is
            where the price and the CTA live. */}
        {lockedCounts.hasRevenue && (
          <LockedSection
            title="what the first page turns over"
            prompt={`Our estimate of weekly turnover across the first page in ${destinationName}`}
            rows={2}
          />
        )}
        {lockedCounts.operators > 0 && (
          <LockedSection
            title="every operator on this page"
            prompt={`All ${lockedCounts.operators} operators competing in ${destinationName}, with each one's share of estimated turnover`}
            rows={lockedCounts.operators}
          />
        )}
        <LockedSection
          title="the rest of the first page"
          prompt={`All ${lockedCounts.tours} ranked tours, not the ${topTours.length} shown above`}
          rows={lockedCounts.tours}
        />
        {lockedCounts.movers > 0 && (
          <LockedSection
            title="this month's movers"
            prompt={`${lockedCounts.movers} ${lockedCounts.movers === 1 ? "tour" : "tours"} changed position in ${destinationName} this month`}
            rows={lockedCounts.movers}
          />
        )}
        {lockedCounts.insights > 0 && (
          <LockedSection
            title="what the page tells you"
            prompt={`${lockedCounts.insights} ${lockedCounts.insights === 1 ? "thing" : "things"} worth knowing about this market`}
            rows={lockedCounts.insights}
          />
        )}
        {lockedCounts.poiSections > 0 && (
          <LockedSection
            title="every point of interest"
            prompt={`Full first-page leaderboards for all ${lockedCounts.poiSections} ${lockedCounts.poiSections === 1 ? "point of interest" : "points of interest"} in ${destinationName}`}
            rows={lockedCounts.poiSections}
          />
        )}

        {/* The considered ask: stated once, after the manifest has named
            everything it covers. Every figure is server-supplied — the same
            counts the locked blocks are sized from — so it cannot overstate
            what is behind it. The sticky bar below is this same ask following
            the reader, never an additional one. */}
        {faq}
        <PurchaseAsk
          ref={askRef}
          destinationName={destinationName}
          lockedCounts={lockedCounts}
          onUnlock={handleUnlockClick}
          isSubmitting={isSubmitting}
          error={error}
          cityCopy={isCityCopy}
        />

        {/* Below the ask, deliberately. This is the free monthly nudge, not
            the purchase — it is secondary voice so it cannot compete. */}
        <LeadGateForm slug={slug} destinationName={destinationName} />
      </Stack>

      <StickyPurchaseBar
        destinationName={destinationName}
        price={CITY_REPORT_PRICE}
        armRef={manifestRef}
        askRef={askRef}
        onUnlock={handleUnlockClick}
        isSubmitting={isSubmitting}
        error={error}
      />
    </>
  );
}

/**
 * The considered commercial ask on a locked report.
 *
 * Replaces five filled "Unlock the full report" buttons — one per locked block
 * — which put three identical CTAs in one viewport and read as nagging rather
 * than persuasion. The manifest above is built from the same server counts the
 * blurred blocks are sized from, so the ask enumerates exactly what is behind
 * the wall instead of asserting value.
 *
 * Forwards a ref so the sticky bar can stand down while this is on screen —
 * the page still shows exactly one filled CTA at a time.
 */
const PurchaseAsk = forwardRef<
  HTMLDivElement,
  {
    destinationName: string;
    lockedCounts: PublicDestinationReportTeaser["lockedCounts"];
    onUnlock: () => void;
    isSubmitting: boolean;
    error: string | null;
    cityCopy: boolean;
  }
>(function PurchaseAsk(
  { destinationName, lockedCounts, onUnlock, isSubmitting, error, cityCopy },
  ref
): ReactElement {
  /* Honesty rule: every line is a real server count or it is not rendered.
     Nothing here is a rounded-up marketing figure.

     Terse on purpose. Each locked block above already states its own case in
     a full sentence; repeating those sentences here would make the ask read
     like a generated summary of the page it sits on. */
  const included: string[] = [`All ${lockedCounts.tours} ranked tours`];
  if (lockedCounts.operators > 0) {
    included.push(`Every one of the ${lockedCounts.operators} operators, with turnover share`);
  }
  if (lockedCounts.movers > 0) {
    included.push(`${lockedCounts.movers} position ${lockedCounts.movers === 1 ? "change" : "changes"} this month`);
  }
  if (lockedCounts.insights > 0) {
    included.push(`${lockedCounts.insights} ${lockedCounts.insights === 1 ? "reading" : "readings"} of what the page is doing`);
  }
  if (lockedCounts.poiSections > 0) {
    included.push(
      `Full leaderboards for all ${lockedCounts.poiSections} ${lockedCounts.poiSections === 1 ? "point of interest" : "points of interest"}`
    );
  }
  included.push("A new issue every month, kept in your library");

  return (
    <Box
      ref={ref}
      borderTop="1px solid var(--color-border-strong)"
      pt={{ base: 8, md: 10 }}
    >
      <Text
        fontFamily="var(--font-geist-mono)"
        fontSize="11px"
        fontWeight="600"
        letterSpacing="0.06em"
        textTransform="uppercase"
        color="var(--color-text-tertiary)"
        mb={3}
      >
        the full {destinationName} report
      </Text>

      {/* Close tier, per design.md § Heading scale. Sits alone. */}
      <Heading
        as="h2"
        fontFamily="var(--font-display)"
        fontSize={{ base: "22px", md: "28px" }}
        fontWeight="600"
        letterSpacing="-0.02em"
        lineHeight="1.15"
        color="var(--color-text-primary)"
        maxW="24ch"
        /* Without this the 32-character line breaks after "holds" and strands
           "back." alone on the second line. */
        style={{ textWrap: "balance" }}
      >
        {cityCopy ? purchaseHeading(destinationName) : "Everything this page holds back."}
      </Heading>

      {cityCopy && (
        <Text
          mt={3}
          fontSize={{ base: "15px", md: "16px" }}
          lineHeight="1.6"
          color="var(--color-text-secondary)"
          maxW="52ch"
        >
          {purchaseLede()}
        </Text>
      )}

      <Stack as="ul" gap={2} mt={6} listStyleType="none">
        {included.map((line) => (
          <Flex as="li" key={line} gap={3} alignItems="baseline">
            <Box
              as="span"
              aria-hidden="true"
              w="5px"
              h="5px"
              mt="7px"
              flexShrink={0}
              borderRadius="full"
              bg="var(--color-primary)"
            />
            <Text
              fontSize={{ base: "15px", md: "16px" }}
              color="var(--color-text-secondary)"
              lineHeight="1.6"
            >
              {line}
            </Text>
          </Flex>
        ))}
      </Stack>

      <Flex
        mt={8}
        gap={{ base: 4, md: 6 }}
        alignItems={{ base: "flex-start", md: "center" }}
        direction={{ base: "column", md: "row" }}
        flexWrap="wrap"
      >
        <Button
          onClick={onUnlock}
          loading={isSubmitting}
          disabled={isSubmitting}
          bg="var(--color-primary)"
          color="var(--color-on-primary)"
          fontWeight="600"
          fontSize="16px"
          h={cityCopy ? "auto" : "48px"}
          minH="48px"
          py={cityCopy ? 3 : undefined}
          px={7}
          borderRadius="var(--radius-sm)"
          whiteSpace={cityCopy ? "normal" : "nowrap"}
          textAlign="center"
          transition="var(--transition-fast)"
          _hover={{ bg: "var(--color-primary-hover)" }}
          _active={{ bg: "var(--color-primary-strong)" }}
        >
          {cityCopy
            ? purchaseButtonLabel(destinationName, CITY_REPORT_PRICE)
            : "Unlock the full report"}
        </Button>
        {/* Mono, because it is a price — design.md § Typography. */}
        <Text
          fontFamily="var(--font-geist-mono)"
          fontSize="14px"
          color="var(--color-text-secondary)"
          style={{ fontVariantNumeric: "tabular-nums" }}
        >
          {CITY_REPORT_PRICE} a month · cancel any time
        </Text>
      </Flex>

      {/* Not a bullet: the list above is a strict inventory of server counts,
          and this is a product fact about what paying does. */}
      <Text
        mt={4}
        fontSize={{ base: "13px", md: "14px" }}
        color="var(--color-text-tertiary)"
        lineHeight="1.6"
      >
        Paying makes you an account — we use the email you pay with.
      </Text>

      {cityCopy && (
        <Link href={CITY_REPORT_PRICING_LINK.href}>
          <Text
            mt={3}
            fontSize="15px"
            color="var(--color-primary)"
            _hover={{ textDecoration: "underline" }}
          >
            {CITY_REPORT_PRICING_LINK.label}
          </Text>
        </Link>
      )}

      {error && (
        <Text
          role="alert"
          mt={4}
          fontSize="14px"
          color="rgb(220, 38, 38)"
        >
          {error}
        </Text>
      )}
    </Box>
  );
});
